import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import type { MissionTypeSettings } from "@shared/schema";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PlanningMission {
  id: string;
  titre: string;
  type: string;
  statut: string;
  start_date: string;
  end_date: string;
  heure_debut: string | null;
  heure_fin: string | null;
  technicien_ids: string[];
  commercial_ids: string[];
  robots: { id: string; sn: string; modele: string }[];
  vehicules: { id: string; plaque: string; modele: string }[];
}

export interface CalendarWeekSyncReport {
  created: number;
  updated: number;
  deleted: number;
  unchanged: number;
  skipped: number;
  errors: { missionId: string; title: string; message: string }[];
  warnings: { missionId: string; title: string; messages: string[] }[];
}

// ── Hooks ──────────────────────────────────────────────────────────────────────

export function useMissionTypeSettings() {
  return useQuery<MissionTypeSettings[]>({
    queryKey: ["/api/mission-type-settings"],
    queryFn: async () => {
      const res = await authFetch("/api/mission-type-settings");
      if (!res.ok) throw new Error("Erreur chargement paramètres types");
      return res.json();
    },
  });
}

export function useUpdateMissionTypeSetting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ typeKey, libelle, couleur }: { typeKey: string; libelle?: string; couleur?: string }) => {
      const res = await authFetch(`/api/mission-type-settings/${typeKey}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ libelle, couleur }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur mise à jour");
      }
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["/api/mission-type-settings"] }),
  });
}

export function usePlanningWeek(startDate: string | null) {
  return useQuery<PlanningMission[]>({
    queryKey: ["/api/planning/semaine", startDate],
    queryFn: async () => {
      if (!startDate) return [];
      const res = await authFetch(`/api/planning/semaine?start=${startDate}`);
      if (!res.ok) throw new Error("Erreur chargement planning");
      return res.json();
    },
    enabled: !!startDate,
  });
}

export function useSyncPlanningWeekCalendar() {
  return useMutation<CalendarWeekSyncReport, Error, string>({
    mutationFn: async (start) => {
      const res = await authFetch("/api/planning/semaine/sync-calendar", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ start }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload.message || "Erreur de synchronisation Google Agenda");
      return payload;
    },
  });
}
